import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';

import { AccountingNode, CostCenter } from '../../../../../types';
import { ProcessedEntry } from '../types';

interface LinesModalProps {
  open: boolean;
  onClose: () => void;
  entry: ProcessedEntry | null;
  nodes: AccountingNode[];
  costCenters: CostCenter[];
}

const LinesModal: React.FC<LinesModalProps> = ({
  open,
  onClose,
  entry,
  nodes,
  costCenters,
}) => {
  const getNodeKey = (line: ProcessedEntry['entry']['lines'][0]): string => {
    if (line.nodeId) {
      const node = nodes.find((n) => n.id === line.nodeId);
      return node?.key || '-';
    }
    // Si no hay nodeId pero hay node (cuando la cuenta no se encontró)
    return line.node?.key || '-';
  };

  const getCostCenterName = (
    costCenterId: number | null | undefined,
  ): string => {
    if (!costCenterId) return '-';
    const costCenter = costCenters.find((cc) => cc.id === costCenterId);
    return costCenter?.name || '-';
  };

  if (!entry) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          Líneas del Asiento: {entry.entry.name}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Fecha: {entry.entry.date} | Total de líneas:{' '}
          {entry.entry.lines.length}
        </Typography>
        {entry.errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Errores encontrados:
            </Typography>
            {entry.errors.map((error, idx) => (
              <Typography key={idx} variant="body2">
                • {error}
              </Typography>
            ))}
          </Alert>
        )}
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Cuenta</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Débito</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Crédito</strong>
                </TableCell>
                <TableCell>
                  <strong>Descripción</strong>
                </TableCell>
                <TableCell>
                  <strong>Centro de Costo</strong>
                </TableCell>
                <TableCell>
                  <strong>RUT</strong>
                </TableCell>
                <TableCell>
                  <strong>Número Doc.</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entry.entry.lines.map((line, lineIndex) => (
                <TableRow key={lineIndex} hover>
                  <TableCell>{getNodeKey(line)}</TableCell>
                  <TableCell align="right">{formatMoney(line.debit)}</TableCell>
                  <TableCell align="right">
                    {formatMoney(line.credit)}
                  </TableCell>
                  <TableCell>{line.description || '-'}</TableCell>
                  <TableCell>{getCostCenterName(line.costCenterId)}</TableCell>
                  <TableCell>{line.businessIdentifier || '-'}</TableCell>
                  <TableCell>{line.documentKey || '-'}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={1}>
                  <strong>Total</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>
                    {formatMoney(
                      entry.entry.lines.reduce(
                        (sum, line) => sum + line.debit,
                        0,
                      ),
                    )}
                  </strong>
                </TableCell>
                <TableCell align="right">
                  <strong>
                    {formatMoney(
                      entry.entry.lines.reduce(
                        (sum, line) => sum + line.credit,
                        0,
                      ),
                    )}
                  </strong>
                </TableCell>
                <TableCell colSpan={4}></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={onClose} color="secondary">
          Cerrar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default LinesModal;
